"use client";

import { ArrowRight } from "lucide-react";
import { EvalCheck, EvalResult, EvalTone, SubRating } from "@/lib/types";
import { selectExpandedChecks } from "./evalDensity";
import { deriveFeedbackGate } from "./evalGate";
import { emphasis } from "./inlineEmphasis";

interface EvalResultViewProps {
  data: EvalResult;
  accent?: AccentColor;
  showStars?: boolean;
  // "adaptive" (default) collapses passed checks to label+rating chips and only
  // expands the bodies that carry a correction. "full" shows every check's body.
  density?: "full" | "adaptive";
  // Optional: the learner's submission, rendered as a quiet pair inside the card
  // header area so it no longer needs a separate recap box in the runner.
  attempt?: { before?: string; after: string };
  // When provided, the card header shows the gate-honest framing (emerald/amber
  // eyebrow "N of M strong") driven by THIS verdict, so it always matches the
  // runner's own pass/continue button. When omitted, the header keeps the
  // tone-coloured look so tone-gated runners that haven't migrated are unchanged.
  passed?: boolean;
}

const ratingStars: Record<SubRating, number> = {
  Excellent: 5,
  Strong: 4,
  Developing: 3,
  Emerging: 2,
  "Needs work": 1,
};

const StarRow = ({ rating }: { rating: SubRating }) => {
  const filled = ratingStars[rating];
  return (
    <span className="text-sm tracking-tight text-amber-500" aria-label={`${filled} of 5 stars`}>
      {"★".repeat(filled)}
      <span className="text-gray-300">{"★".repeat(5 - filled)}</span>
    </span>
  );
};

type AccentColor =
  | "indigo"
  | "violet"
  | "pink"
  | "sky"
  | "amber"
  | "rose"
  | "yellow"
  | "cyan"
  | "emerald"
  | "orange"
  | "fuchsia";

const ratingChip: Record<SubRating, string> = {
  Excellent: "bg-emerald-100 text-emerald-700",
  Strong: "bg-sky-100 text-sky-700",
  Developing: "bg-amber-100 text-amber-700",
  Emerging: "bg-orange-100 text-orange-700",
  "Needs work": "bg-red-100 text-red-700",
};

const toneStyle: Record<EvalTone, string> = {
  great: "bg-emerald-50 border-emerald-200 text-emerald-900",
  good: "bg-sky-50 border-sky-200 text-sky-900",
  "needs-work": "bg-amber-50 border-amber-200 text-amber-900",
};

// Brand-family accents (indigo/violet/fuchsia) resolve to the theme token so
// they adapt to the adult theme; the other hues stay as per-tool identity.
const accentBorder: Record<AccentColor, string> = {
  indigo: "border-brand-soft",
  violet: "border-brand-soft",
  pink: "border-pink-200",
  sky: "border-sky-200",
  amber: "border-amber-200",
  rose: "border-rose-200",
  yellow: "border-yellow-200",
  cyan: "border-cyan-200",
  emerald: "border-emerald-200",
  orange: "border-orange-200",
  fuchsia: "border-brand-soft",
};

const accentLeft: Record<AccentColor, string> = {
  indigo: "border-brand",
  violet: "border-brand",
  pink: "border-pink-300",
  sky: "border-sky-300",
  amber: "border-amber-300",
  rose: "border-rose-300",
  yellow: "border-yellow-300",
  cyan: "border-cyan-300",
  emerald: "border-emerald-300",
  orange: "border-orange-300",
  fuchsia: "border-brand",
};

const CheckRow = ({ check, showStars }: { check: EvalCheck; showStars: boolean }) => (
  <div className="flex flex-wrap items-center gap-2">
    <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{check.label}</p>
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ratingChip[check.rating]}`}>
      {check.rating}
    </span>
    {showStars && <StarRow rating={check.rating} />}
  </div>
);

// Rubric checks rendered inline inside the card (no inner box). Passed axes
// collapse to a chip row in "adaptive" mode; only checks carrying a correction
// keep their body paragraph.
const ChecksInline = ({
  checks,
  accent,
  showStars,
  density,
}: {
  checks: EvalCheck[];
  accent: AccentColor;
  showStars: boolean;
  density: "full" | "adaptive";
}) => {
  const expanded = density === "adaptive" ? selectExpandedChecks(checks) : checks.map(() => true);
  const collapsed = checks.filter((_, i) => !expanded[i]);
  const shown = checks.filter((_, i) => expanded[i]);

  return (
    <div className="space-y-3">
      {collapsed.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {collapsed.map((c, i) => (
            <CheckRow key={i} check={c} showStars={showStars} />
          ))}
        </div>
      )}
      {shown.map((c, i) => (
        <div key={i} className={`border-l-2 ${accentLeft[accent]} pl-3`}>
          <div className="mb-1">
            <CheckRow check={c} showStars={showStars} />
          </div>
          <p className="text-sm leading-relaxed whitespace-pre-line text-gray-700">
            {emphasis(c.body)}
          </p>
        </div>
      ))}
    </div>
  );
};

const AttemptPair = ({ attempt }: { attempt: NonNullable<EvalResultViewProps["attempt"]> }) =>
  attempt.before ? (
    <p className="text-sm leading-relaxed">
      <span className="text-gray-400 line-through">{attempt.before}</span>
      <ArrowRight className="mx-2 inline h-4 w-4 text-gray-400" aria-hidden="true" />
      <span className="font-semibold text-gray-900">{attempt.after}</span>
    </p>
  ) : (
    <p className="text-sm leading-relaxed font-semibold text-gray-900">{attempt.after}</p>
  );

export const EvalResultView = ({
  data,
  accent = "indigo",
  showStars = false,
  density = "adaptive",
  attempt,
  passed,
}: EvalResultViewProps) => {
  // The honest gate header is opt-in: a runner passes `passed` (its own
  // pass/continue verdict) so the header framing always agrees with its button.
  // Runners that don't pass it keep the original tone-coloured header, so
  // tone-gated tools (which gate on tonePass, not weakest-link) are unchanged.
  const gate = deriveFeedbackGate(data);
  const honest = passed !== undefined;
  const headerTone = honest
    ? passed
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : "bg-amber-50 border-amber-200 text-amber-900"
    : toneStyle[data.tone];

  return (
    <div className={`overflow-hidden rounded-xl border ${accentBorder[accent]}`}>
      {/* Header — gate-honest framing when `passed` is supplied, else tone-coloured.
          The eyebrow (lead + "N of M strong") is built inline so it's only
          computed on the opt-in path. */}
      <div className={`border-b p-4 ${headerTone}`}>
        {honest && (
          <p
            className={`mb-1 text-xs font-semibold tracking-wide uppercase ${
              passed ? "text-emerald-700" : "text-amber-800"
            }`}
          >
            {gate.total > 0
              ? `${passed ? "✓ Nice work" : "◑ Almost there"} · ${gate.strongCount} of ${gate.total} strong`
              : passed
                ? "✓ Nice work"
                : "◑ Almost there"}
          </p>
        )}
        <p className="text-base leading-snug font-bold">{emphasis(data.verdict)}</p>
      </div>

      {/* Body — one column, internal sections instead of floating boxes */}
      <div className="space-y-3 bg-white p-4">
        {attempt && <AttemptPair attempt={attempt} />}

        {data.checks.length > 0 && (
          <ChecksInline
            checks={data.checks}
            accent={accent}
            showStars={showStars}
            density={density}
          />
        )}

        {data.watchOut && (
          <div className="border-l-2 border-amber-300 pl-3">
            <p className="mb-1 text-xs font-semibold tracking-wide text-amber-800 uppercase">
              ⚠ Watch out
            </p>
            <p className="text-sm leading-relaxed whitespace-pre-line text-amber-900">
              {emphasis(data.watchOut)}
            </p>
          </div>
        )}

        {data.improvement && (
          <div className="border-l-2 border-emerald-300 pl-3">
            <p className="mb-1 text-xs font-semibold tracking-wide text-emerald-700 uppercase">
              🎯 {data.improvement.heading}
            </p>
            <p className="text-base leading-relaxed text-emerald-950 italic">
              &ldquo;{data.improvement.example}&rdquo;
            </p>
            <p className="mt-1 text-xs leading-relaxed text-emerald-800">
              {emphasis(data.improvement.why)}
            </p>
          </div>
        )}

        {data.alternatives && data.alternatives.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-800">
              {data.alternativesTitle ?? "Alternatives"}
            </p>
            {data.alternatives.map((a, i) => (
              <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="mb-1 text-xs font-semibold text-gray-700">{a.heading}</p>
                <p className="mb-1 text-sm text-gray-900 italic">&ldquo;{a.example}&rdquo;</p>
                {a.note && <p className="text-xs leading-relaxed text-gray-500">{a.note}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The per-item reflective `closing` question is intentionally not rendered. */}
    </div>
  );
};
